import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, MotionConfig } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';

const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const { updatePassword } = useAuth();

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => navigate('/signin'), 3000);
    return () => clearTimeout(timer);
  }, [success, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    const { error: updateError } = await updatePassword(formData.password);

    if (updateError) {
      setError(updateError.message);
      setLoading(false);
    } else {
      setLoading(false);
      setSuccess(true);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const inputClass =
    'w-full rounded-lg border border-black/[0.08] bg-white px-4 py-3 text-sm text-charcoal placeholder-ink-muted/60 transition-colors focus:border-charcoal/30 focus:outline-none focus:ring-2 focus:ring-charcoal/10';

  return (
    <MotionConfig reducedMotion="user">
      <div className="relative isolate flex min-h-screen w-full items-center justify-center bg-white px-6 py-16">
        {/* Blueprint grid */}
        <div aria-hidden className="blueprint-grid pointer-events-none absolute inset-0 z-0" />

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: EASE }}
          className="relative z-10 w-full max-w-sm"
        >
          {success ? (
            <>
              <div className="mb-8 text-center">
                <span className="inline-block text-xs font-semibold uppercase tracking-eyebrow text-ink-muted">
                  Steelman
                </span>
                <h1 className="mt-3 font-serif text-4xl tracking-tight text-charcoal">
                  Password updated.
                </h1>
                <p className="mt-2 text-sm text-ink-muted">
                  You can sign in with your new password now.
                </p>
              </div>

              <div className="rounded-2xl border border-black/[0.07] bg-white/90 p-8 text-center shadow-frame backdrop-blur-sm">
                <Link to="/signin" className="btn-primary w-full">
                  Sign in
                </Link>
                <p className="mt-4 text-xs text-ink-muted">
                  Redirecting you to sign in shortly…
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="mb-8 text-center">
                <span className="inline-block text-xs font-semibold uppercase tracking-eyebrow text-ink-muted">
                  Steelman
                </span>
                <h1 className="mt-3 font-serif text-4xl tracking-tight text-charcoal">
                  Set a new password.
                </h1>
                <p className="mt-2 text-sm text-ink-muted">
                  Choose something strong — at least 8 characters.
                </p>
              </div>

              <div className="rounded-2xl border border-black/[0.07] bg-white/90 p-8 shadow-frame backdrop-blur-sm">
                <form onSubmit={handleSubmit} className="space-y-5">
                  <AnimatePresence>
                    {error && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.25, ease: EASE }}
                        className="overflow-hidden rounded-lg border border-black/[0.08] bg-charcoal/[0.04] px-4 py-3 text-sm text-charcoal"
                      >
                        {error}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div>
                    <label
                      htmlFor="password"
                      className="mb-1.5 block text-xs font-semibold uppercase tracking-eyebrow text-ink-muted"
                    >
                      New password
                    </label>
                    <input
                      type="password"
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      minLength={8}
                      placeholder="••••••••"
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="confirmPassword"
                      className="mb-1.5 block text-xs font-semibold uppercase tracking-eyebrow text-ink-muted"
                    >
                      Confirm new password
                    </label>
                    <input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required
                      placeholder="••••••••"
                      className={inputClass}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary mt-1 w-full disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:shadow-none"
                  >
                    {loading ? 'Updating…' : 'Update password'}
                  </button>
                </form>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </MotionConfig>
  );
};

export default ResetPassword;
